import {
  Session,
  SessionList,
  SessionWithLogs,
  CreateSessionRequest,
  ElapsedRequest,
} from "./schemas";

const API_BASE = process.env.BUN_PUBLIC_API_URL;

let getAuthToken: (() => Promise<string | null>) | null = null;

export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  getAuthToken = getter;
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  if (!getAuthToken) return {};
  
  const token = await getAuthToken();
  if (!token) return {};
  
  return { Authorization: `Bearer ${token}` };
}

async function request<T>(
  path: string,
  options: RequestInit,
  schema: { parse: (data: unknown) => T }
): Promise<T> {
  const authHeaders = await getAuthHeaders();
  
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new ApiError(response.status, `Request failed: ${response.statusText}`);
  }

  const data = await response.json();
  return schema.parse(data);
}

async function requestVoid(path: string, options: RequestInit): Promise<void> {
  const authHeaders = await getAuthHeaders();
  
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new ApiError(response.status, `Request failed: ${response.statusText}`);
  }
}

export function listSessions(): Promise<Session[]> {
  return request("/sessions", { method: "GET" }, SessionList);
}

export function getSession(id: string): Promise<SessionWithLogs> {
  return request(`/sessions/${id}`, { method: "GET" }, SessionWithLogs);
}

export function createSession(data: CreateSessionRequest): Promise<Session> {
  CreateSessionRequest.parse(data);
  return request(
    "/sessions",
    { method: "POST", body: JSON.stringify(data) },
    Session
  );
}

export function deleteSession(id: string): Promise<void> {
  return requestVoid(`/sessions/${id}`, { method: "DELETE" });
}

export function startSession(id: string, data: ElapsedRequest): Promise<Session> {
  ElapsedRequest.parse(data);
  return request(
    `/sessions/${id}/start`,
    { method: "POST", body: JSON.stringify(data) },
    Session
  );
}

export function pauseSession(id: string, data: ElapsedRequest): Promise<Session> {
  ElapsedRequest.parse(data);
  return request(
    `/sessions/${id}/pause`,
    { method: "POST", body: JSON.stringify(data) },
    Session
  );
}

export function resumeSession(id: string, data: ElapsedRequest): Promise<Session> {
  ElapsedRequest.parse(data);
  return request(
    `/sessions/${id}/resume`,
    { method: "POST", body: JSON.stringify(data) },
    Session
  );
}

export function stopSession(id: string, data: ElapsedRequest): Promise<Session> {
  ElapsedRequest.parse(data);
  return request(
    `/sessions/${id}/stop`,
    { method: "POST", body: JSON.stringify(data) },
    Session
  );
}

export { ApiError };
